import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button} from '@mui/material';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import ArrowDownwardOutlinedIcon from '@mui/icons-material/ArrowDownwardOutlined';
import FormatIndentIncreaseIcon from '@mui/icons-material/FormatIndentIncrease';
import FormatIndentDecreaseIcon from '@mui/icons-material/FormatIndentDecrease';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import AdmicityDialog from '../../../../shared-components/AdmicityDialog';
import AdmicityForm from '../../../../shared-components/AdmicityForm';
import useNotification from '../../../../utility/hooks/useNotification';
import SectionHeading from './SectionHeading';
import {
    useGetAdmissionCategoriesQuery,
    useManageAdmissionCategoriesMutation
} from '../../../../api/services/lookupsService';
import {normaliseRows, resolveNumbers} from '../../../../utility/admissionCategories';

const toRows = categories => {
    const byId = new Map(categories.map(c => [c.id, c]));

    return categories.map(c => ({
        id: c.id,
        shortDescription: c.shortDescription,
        description: c.description,
        isSubPart: Boolean(c.parentId && byId.has(c.parentId)),
        isEhcp: Boolean(c.isEhcp)
    }));
};

const AdmissionCategorySettings = () => {
    const {data: categories = [], refetch} = useGetAdmissionCategoriesQuery();
    const [manageCategories, {isSuccess, isError}] = useManageAdmissionCategoriesMutation();
    const {showErrorNotification, showSuccessNotification} = useNotification();
    const [rows, setRows] = useState([]);
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const [openEditDialog, setOpenEditDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [saveValidationError, setSaveValidationError] = useState('');
    const formRef = useRef(null);

    useEffect(() => {
        setRows(toRows(categories));
    }, [categories]);

    useEffect(() => {
        if (isError) {
            showErrorNotification('Failed to update the admissions categories');
        } else if (isSuccess) {
            refetch();
            showSuccessNotification('Admissions categories updated successfully');
        }
    }, [isSuccess, isError]);

    const numbers = resolveNumbers(rows);

    const textFields = [
        {
            label: 'Short description',
            name: 'shortDescription',
            required: true,
            initialValue: selectedRow?.shortDescription ?? undefined,
            validate: value => ({
                isValid: Boolean(value && value.trim()) && value.length <= 100,
                message: !value || !value.trim()
                    ? 'Please provide a short description, such as Sibling or Catchment.'
                    : 'A short description cannot exceed 100 characters.'
            })
        },
        {
            label: 'Criterion',
            name: 'description',
            required: true,
            multiline: true,
            rows: 3,
            initialValue: selectedRow?.description ?? undefined,
            validate: value => ({
                isValid: Boolean(value && value.trim()) && value.length <= 1000,
                message: !value || !value.trim()
                    ? 'Please provide the criterion as a parent will read it on the form.'
                    : 'A criterion cannot exceed 1000 characters.'
            })
        }
    ];

    const hasEhcp = Boolean(rows[0]?.isEhcp);
    const firstMovable = hasEhcp ? 1 : 0;

    const move = (index, targetIndex) => {
        if (targetIndex < firstMovable || targetIndex >= rows.length || rows[index].isEhcp) {
            return;
        }

        const next = [...rows];
        const [item] = next.splice(index, 1);
        next.splice(targetIndex, 0, item);
        setRows(normalise(next));
    };

    // A sub-part cannot hold sub-parts of its own.
    const normalise = normaliseRows;

    const setSubPart = (index, isSubPart) =>
        setRows(normalise(rows.map((row, i) => i === index ? {...row, isSubPart} : row)));

    // Anything below the first ordinary category can become a part of the category above it.
    const canBeSubPart = index => index > firstMovable && !rows[index].isEhcp;

    // Category 0 is added on its own button; it reads 0 and stays at the top. A school writes its words.
    const [addingZero, setAddingZero] = useState(false);

    const handleAdd = payload => {
        setRows(normalise([...rows, {
            id: null,
            shortDescription: payload.shortDescription,
            description: payload.description,
            isSubPart: false,
            isEhcp: addingZero
        }]));
        setAddingZero(false);
        setSaveValidationError('');
        handleFinishEditing();
    };

    const handleEdit = payload => {
        setRows(rows.map(row => row === selectedRow
            ? {...row, shortDescription: payload.shortDescription, description: payload.description}
            : row));
        setSaveValidationError('');
        handleFinishEditing();
    };

    const handleFinishEditing = () => {
        setOpenAddDialog(false);
        setOpenEditDialog(false);
        setSelectedRow(null);
    };

    const getListValidationError = () => {
        const seen = new Set();

        for (const row of rows) {
            if (!row.shortDescription?.trim() || !row.description?.trim()) {
                return 'Every category needs a short description and a criterion.';
            }

            const key = row.shortDescription.trim().toLowerCase();

            if (seen.has(key)) {
                return 'Two categories cannot share the same short description.';
            }

            seen.add(key);
        }

        return '';
    };

    const handleSave = () => {
        const validationError = getListValidationError();

        setSaveValidationError(validationError);

        if (validationError) {
            return;
        }

        let lastCategoryPosition = null;

        manageCategories({
            categories: rows.map((row, index) => {
                const parentPosition = row.isSubPart ? lastCategoryPosition : null;

                if (!row.isSubPart) {
                    lastCategoryPosition = index;
                }

                const parent = parentPosition !== null ? rows[parentPosition] : null;

                return {
                    id: row.id,
                    shortDescription: row.shortDescription,
                    description: row.description,
                    parentId: parent?.id ?? null,
                    parentPosition: parent && parent.id ? null : parentPosition,
                    isEhcp: Boolean(row.isEhcp)
                };
            })
        });
    };

    return (
        <Box>
            <SectionHeading
                title="Admissions Categories"
                testId="admissions-categories-heading"
            />
            <Box marginTop={2} marginBottom={2}>
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: {xs: '100%', sm: 'auto'},
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    startIcon={<AddCircleOutlineIcon/>}
                    onClick={() => {
                        setSelectedRow(null);
                        setAddingZero(false);
                        setOpenAddDialog(true);
                    }}
                >
                    Add new
                </Button>
                {!hasEhcp && (
                    <Button
                        sx={{ml: {xs: 0, sm: 2}, mt: {xs: 1, sm: 0}, width: {xs: '100%', sm: 'auto'}}}
                        variant="outlined"
                        data-testid="add-category-zero"
                        onClick={() => {
                            setSelectedRow(null);
                            setAddingZero(true);
                            setOpenAddDialog(true);
                        }}
                    >
                        Add category 0
                    </Button>
                )}
            </Box>
            <List disablePadding sx={{width: '100%'}}>
                {rows.map((row, index) => (
                    <ListItem
                        key={`admissionCategory-${index}`}
                        sx={{
                            px: 1,
                            py: 0.25,
                            pl: row.isSubPart ? 5 : 1,
                            backgroundColor: index % 2 === 0 ? '#F0FBFA' : '#FFFFFF',
                            '&:hover': {backgroundColor: '#D4F4F0'}
                        }}
                    >
                        <ListItemText
                                sx={{my: 0}}
                            primary={
                                <Stack flexDirection="row" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography>
                                            <Box component="span" fontWeight="bold" mr={1}>{numbers[index]}</Box>
                                            {row.shortDescription}
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                            sx={{whiteSpace: 'pre-line'}}
                                        >
                                            {row.description}
                                        </Typography>

                                    </Box>
                                    <Box display="flex">
                                        <IconButton
                                            size="small"
                                            disabled={index <= firstMovable || row.isEhcp}
                                            onClick={() => move(index, index - 1)}
                                            aria-label="Move category up"
                                        >
                                            <ArrowUpwardOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={index === rows.length - 1 || row.isEhcp}
                                            onClick={() => move(index, index + 1)}
                                            aria-label="Move category down"
                                        >
                                            <ArrowDownwardOutlinedIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={row.isSubPart || !canBeSubPart(index)}
                                            onClick={() => setSubPart(index, true)}
                                            aria-label="Make this a part of the category above"
                                        >
                                            <FormatIndentIncreaseIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            disabled={!row.isSubPart}
                                            onClick={() => setSubPart(index, false)}
                                            aria-label="Make this a category of its own"
                                        >
                                            <FormatIndentDecreaseIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                setSelectedRow(row);
                                                setOpenEditDialog(true);
                                            }}
                                        >
                                            <EditIcon fontSize="small"/>
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            onClick={() => setRows(normalise(rows.filter(r => r !== row)))}
                                        >
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </Box>
                                </Stack>
                            }
                        />
                    </ListItem>
                ))}
            </List>
            <Box display="flex" justifyContent="end" py={2}>
                {saveValidationError
                    ? <Alert severity="error" sx={{width: '100%', mr: {xs: 0, sm: 2}, mb: {xs: 2, sm: 0}}}>
                        {saveValidationError}
                    </Alert>
                    : null}
                <Button
                    sx={{
                        backgroundColor: '#186BBE',
                        boxShadow: 'none',
                        width: {xs: '100%', sm: 'auto'},
                        '&:hover': {
                            backgroundColor: '#155FA7',
                            boxShadow: 'none'
                        }
                    }}
                    variant="contained"
                    onClick={handleSave}
                >
                    Save
                </Button>
            </Box>
            <AdmicityDialog
                handleClose={handleFinishEditing}
                title={openAddDialog ? 'New Admissions Category' : 'Update Admissions Category'}
                open={openAddDialog || openEditDialog}
                actions={[]}
            >
                <AdmicityForm
                    ref={formRef}
                    textFields={textFields}
                    handleSubmit={data => openAddDialog ? handleAdd(data) : handleEdit(data)}
                    buttonText="Save"
                />
            </AdmicityDialog>
        </Box>
    );
};

export default AdmissionCategorySettings;
